import { 
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  writeBatch,
  Firestore,
  DocumentReference,
  QueryDocumentSnapshot,
  startAfter,
  setDoc,
  deleteDoc,
  increment
} from 'firebase/firestore';

import { Message, Conversation, ConversationWithUser } from '@/types/messages';
import { UserProfile } from '@/types/user';
import { getAuth } from 'firebase/auth';
import { ProfileService } from './profileService';
import { db } from '@/firebase/config';
import { Report } from '@/types/reports';
import { generateId } from '@/utils/generateId';
import { BookingSystemMessage } from '@/types/bookings';

// Add new interface for paginated messages response
export interface MessagesResponse {
  messages: Message[];
  nextCursor: string | null;
  hasMore: boolean;
}

// Update the sendMessage interface
export interface SendMessageParams {
  content: string;
  conversationId: string | null;
  recipientId: string;
  senderId: string;
  senderType: "talent" | "promoter";
}

export class MessagingService {
  private db: Firestore;
  
  constructor(db: Firestore) {
    this.db = db;
  }

  /**
   * Start a new conversation between a talent and a promoter
   */
  async startConversation(talentId: string, promoterId: string): Promise<string> {
    
    // Check if conversation already exists
    const existingConvo = await this.findExistingConversation(talentId, promoterId);
    
    if (existingConvo) {

      
      // If conversation exists but was deleted for current user, undelete it
      const auth = getAuth();
      const currentUserId = auth.currentUser?.uid;
      
      if (currentUserId && existingConvo.deletedFor?.includes(currentUserId)) {
        // Remove current user from deletedFor array
        const updatedDeletedFor = existingConvo.deletedFor.filter(id => id !== currentUserId);
        
        await updateDoc(doc(this.db, 'conversations', existingConvo.id), {
          deletedFor: updatedDeletedFor
        });
        
      }
      
      return existingConvo.id;
    }

    // Create new conversation with participantIds
    const sortedParticipantIds = [talentId, promoterId].sort();
    const conversationData = {
      participants: {
        talentId,
        promoterId
      },
      participantIds: [talentId, promoterId],
      sortedParticipantIds,
      unreadCounts: {
        [talentId]: 0,
        [promoterId]: 0
      },
      createdAt: serverTimestamp(),
      deletedFor: [],
      lastMessage: {
        content: null,
        timestamp: null,
        senderId: null
      },
      updatedAt: serverTimestamp()
    };

    try {
      const convoRef = await addDoc(collection(this.db, 'conversations'), conversationData);
      return convoRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Find an existing conversation between two users
   */
  private async findExistingConversation(talentId: string, promoterId: string): Promise<Conversation | null> {
    const participantPair = [talentId, promoterId].sort(); // Sort to ensure consistent order
    
    const q = query(
      collection(this.db, 'conversations'),
      where('sortedParticipantIds', '==', participantPair),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    
    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data()
    } as Conversation;
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage({
    content,
    conversationId,
    recipientId,
    senderId,
    senderType
  }: SendMessageParams): Promise<{ messageId: string; conversationId: string }> {
    let finalConversationId: string;
    
    // If no conversation exists yet, create one
    if (!conversationId) {
      const talentId = senderType === 'talent' ? senderId : recipientId;
      const promoterId = senderType === 'promoter' ? senderId : recipientId;
      finalConversationId = await this.startConversation(talentId, promoterId);
    } else {
      // Check if conversation exists, if not create it
      const existingConvo = await this.findExistingConversation(
        senderType === 'talent' ? senderId : recipientId,
        senderType === 'promoter' ? senderId : recipientId
      );
      
      finalConversationId = existingConvo?.id || await this.startConversation(
        senderType === 'talent' ? senderId : recipientId,
        senderType === 'promoter' ? senderId : recipientId
      );
    }

    const timestamp = serverTimestamp();
    const messagesRef = collection(this.db, 'conversations', finalConversationId, 'messages');
    const messageRef = doc(messagesRef);
    
    const messageData = {
      senderId,
      senderType,
      content,
      timestamp,
      read: false
    };
    
    await setDoc(messageRef, messageData);

    // Update conversation's last message
    await updateDoc(doc(this.db, 'conversations', finalConversationId), {
      lastMessage: {
        content,
        timestamp: Date.now(),
        senderId
      },
      updatedAt: timestamp
    });

    await this.updateUnreadCount(finalConversationId, senderId, recipientId);

    return {
      messageId: messageRef.id,
      conversationId: finalConversationId
    };
  }

  /**
   * Get all conversations for a user (excluding soft-deleted ones)
   */
  async getConversations(userId: string, userType: "talent" | "promoter"): Promise<ConversationWithUser[]> {
    const q = query(
      collection(this.db, 'conversations'),
      where('participantIds', 'array-contains', userId),
      where('deletedFor', 'not-in', [[userId]]) // Exclude conversations deleted by this user
    );

    try {
      const querySnapshot = await getDocs(q);

      const conversations: ConversationWithUser[] = [];

      for (const doc of querySnapshot.docs) {
        const convoData = doc.data() as Conversation;
        
        // Get the other user's ID from participantIds array
        const otherUserId = convoData.participantIds.find(id => id !== userId);
        
        if (otherUserId) {
          const otherUserProfile = await this.getUserProfile(otherUserId);
          
          if (otherUserProfile) {
            conversations.push({
              id: doc.id,
              otherUser: {
                id: otherUserId,
                name: this.getDisplayName(otherUserProfile),
                imageUrl: this.getProfileImage(otherUserProfile)
              },
              lastMessage: convoData.lastMessage
            });
          }
        }
      }

      return conversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Get messages for a specific conversation with pagination
   */
  async getMessages(
    conversationId: string,
    pageSize: number,
    cursor?: string | null
  ): Promise<MessagesResponse> {
    let messagesQuery = query(
      collection(this.db, `conversations/${conversationId}/messages`),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    if (cursor) {
      const cursorDoc = await getDoc(doc(this.db, `conversations/${conversationId}/messages`, cursor));
      if (cursorDoc.exists()) {
        messagesQuery = query(messagesQuery, startAfter(cursorDoc));
      }
    }

    const querySnapshot = await getDocs(messagesQuery);
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toMillis() || Date.now()
    })) as Message[];

    return {
      messages,
      nextCursor: messages[messages.length - 1]?.id || null,
      hasMore: messages.length === pageSize
    };
  }

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    const q = query(
      collection(this.db, `conversations/${conversationId}/messages`),
      where('read', '==', false),
      where('senderId', '!=', userId)
    );

    const querySnapshot = await getDocs(q);
    const batch = writeBatch(this.db);

    querySnapshot.docs.forEach(doc => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
  }

  /**
   * Delete a conversation (soft delete with message handling)
   */
  async deleteConversation(conversationId: string, userId: string): Promise<void> {
    const batch = writeBatch(this.db);
    const convoRef = doc(this.db, 'conversations', conversationId);
    
    // Get the conversation first to check if both users have deleted it
    const convoDoc = await getDoc(convoRef);
    if (!convoDoc.exists()) {
      throw new Error('Conversation not found');
    }

    const convoData = convoDoc.data();
    const deletedFor = convoData.deletedFor || [];
    const updatedDeletedFor = [...deletedFor, userId];
    const allParticipants = convoData.participantIds || [];

    // If all participants have deleted the conversation, hard delete everything
    if (updatedDeletedFor.length === allParticipants.length) {
      // Delete all messages in the subcollection
      const messagesQuery = query(
        collection(this.db, `conversations/${conversationId}/messages`)
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      
      // Delete in chunks of 500 (Firestore batch limit)
      const deleteChunks: Promise<void>[] = [];
      const chunkSize = 500;
      
      for (let i = 0; i < messagesSnapshot.docs.length; i += chunkSize) {
        const chunk = messagesSnapshot.docs.slice(i, i + chunkSize);
        const tempBatch = writeBatch(this.db);
        
        chunk.forEach(doc => {
          tempBatch.delete(doc.ref);
        });
        
        deleteChunks.push(tempBatch.commit());
      }

      // Wait for all message deletions
      await Promise.all(deleteChunks);
      
      // Finally delete the conversation document
      await deleteDoc(convoRef);

    } else {
      // Soft delete - just mark as deleted for this user
      await updateDoc(convoRef, {
        deletedFor: updatedDeletedFor
      });

    }
  }

  /**
   * Get unread message count for conversations
   */
  async getUnreadCount(userId: string): Promise<Record<string, number>> {
    const q = query(
      collection(this.db, 'conversations'),
      where('participantIds', 'array-contains', userId)
    );

    const conversations = await getDocs(q);
    const unreadCounts: Record<string, number> = {};

    conversations.docs.forEach((doc) => {
      const data = doc.data();
      // Use the existing unreadCounts map we're already maintaining
      unreadCounts[doc.id] = data.unreadCounts?.[userId] || 0;
    });

    return unreadCounts;
  }

  /**
   * Toggle unread status for a conversation
   */
  async toggleUnread(conversationId: string, userId: string): Promise<void> {
    const convoRef = doc(this.db, 'conversations', conversationId);
    const convoDoc = await getDoc(convoRef);
    
    if (!convoDoc.exists()) {
      throw new Error('Conversation not found');
    }

    const data = convoDoc.data();
    const currentUnreadStatus = data.unreadBy?.[userId] || false;

    await updateDoc(convoRef, {
      [`unreadBy.${userId}`]: !currentUnreadStatus
    });
  }

  private async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      // We don't have direct access to React Query here, so we'll use the regular service
      // The shared hook will handle caching at the component level
      return await ProfileService.getProfile(userId);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  private getDisplayName(profile: UserProfile): string {
    if (profile.type === 'talent' && profile.talentData) {
      return profile.talentData.name;
    } else if (profile.type === 'promoter' && profile.promoterData) {
      return profile.promoterData.promotionName;
    }
    return 'Unknown User';
  }

  private getProfileImage(profile: UserProfile): string | undefined {
    if (profile.type === 'talent' && profile.talentData) {
      return profile.talentData.imageUrl;
    } else if (profile.type === 'promoter' && profile.promoterData) {
      return profile.promoterData.imageUrl;
    }
    return undefined;
  }

  async updateUnreadCount(
    conversationId: string,
    senderId: string,
    recipientId: string
  ): Promise<void> {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      const convoDoc = await getDoc(conversationRef);
      
      if (!convoDoc.exists()) {
        throw new Error('Conversation not found');
      }

      // Verify the users are participants
      const data = convoDoc.data();
      if (!data.participantIds.includes(senderId) || !data.participantIds.includes(recipientId)) {
        throw new Error('Invalid participants');
      }
      
      await updateDoc(conversationRef, {
        [`unreadCounts.${recipientId}`]: increment(1),
        lastMessageTimestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update unread count:', error);
      throw new Error('Failed to update message status');
    }
  }

  async sendSystemMessage(
    conversationId: string,
    content: BookingSystemMessage
  ): Promise<void> {
    const messageRef = doc(collection(this.db, `conversations/${conversationId}/messages`));
    
    const messageData = {
      type: 'system',
      content,
      timestamp: serverTimestamp(),
      read: false
    };

    await setDoc(messageRef, messageData);

    // Update conversation's last message
    await updateDoc(doc(this.db, 'conversations', conversationId), {
      lastMessage: {
        content: this.getSystemMessagePreview(content),
        timestamp: Date.now(),
        type: 'system'
      },
      updatedAt: serverTimestamp()
    });
  }

  private getSystemMessagePreview(content: BookingSystemMessage): string {
    switch (content.action) {
      case 'created':
        return `New booking request for ${content.details?.eventName || 'Event'}`;
      case 'updated':
        return `Booking request updated for ${content.details?.eventName || 'Event'}`;
      case 'accepted':
        return `Booking request accepted for ${content.details?.eventName || 'Event'}`;
      case 'rejected':
        return `Booking request declined for ${content.details?.eventName || 'Event'}`;
      case 'cancelled':
        return `Booking cancelled for ${content.details?.eventName || 'Event'}`;
      default:
        return 'Booking status updated';
    }
  }
}

export async function reportConversation(conversationId: string, reportedUserId: string): Promise<void> {
  const report: Report = {
    id: generateId(),
    conversationId,
    reportedUserId,
    timestamp: Date.now(),
    status: 'pending'
  };

  await setDoc(doc(db, 'reports', report.id), report);
}